import React, { useState, useMemo, useEffect } from 'react';
import InventoryTable from './InventoryTable';
import InventoryCard from './InventoryCard';
import ItemDetailModal from './ItemDetailModal';
import ReportGeneratorModal from './ReportGeneratorModal';
import IntelligenceHub from './IntelligenceHub';
import AnalyticsModal from './AnalyticsModal';
import { useExcludedItems } from '../hooks/useExcludedItems';
import { useMorningBriefing } from '../hooks/useMorningBriefing';
import { useAuth } from '../hooks/useAuth';
import { Search, Sliders, AlertTriangle, Package, Activity, Calculator, Clock, LogOut, EyeOff, XCircle, MessageCircle, FileText, TrendingUp, AlertOctagon, Sparkles, PieChart } from 'lucide-react';
import { calculateNetStock, calculateDOI, calculateABC, formatNumber, getTrafficLight } from '../utils/calculations';
import { aiAnalyst } from '../utils/aiAnalyst';
import MobileSwipeRow from './MobileSwipeRow';

// --- Bento Grid Stat Card ---
const StatBentoCard = ({ title, value, subtext, type = "neutral", icon: Icon, onClick, isActive }) => {
    const styles = {
        critical: "bg-red-50 border-red-100 text-red-900 ring-red-200",
        warning: "bg-amber-50 border-amber-100 text-amber-900 ring-amber-200",
        success: "bg-emerald-50 border-emerald-100 text-emerald-900 ring-emerald-200",
        neutral: "bg-white border-slate-200 text-slate-900 ring-slate-200"
    };

    const activeRing = isActive ? "ring-2 ring-offset-2" : "";
    const baseStyle = styles[type] || styles.neutral;

    return (
        <div
            onClick={onClick}
            className={`
                relative overflow-hidden rounded-2xl border p-5 cursor-pointer 
                transition-all duration-300 active:scale-95
                ${baseStyle} ${activeRing}
            `}
        >
            <div className="flex justify-between items-start z-10 relative">
                <div className="flex flex-col">
                    <span className="text-xs font-bold uppercase tracking-wider opacity-70 mb-1">{title}</span>
                    <span className="text-3xl font-black tracking-tight font-mono">{value}</span>
                </div>
                <div className={`p-2 rounded-xl bg-white/50 backdrop-blur-sm`}>
                    <Icon size={20} className="opacity-80" />
                </div>
            </div>
            {/* Background decoration */}
            <div className="absolute -bottom-4 -right-4 opacity-5 pointer-events-none">
                <Icon size={80} />
            </div>
        </div>
    );
};

// --- Filter Chip ---
const FilterChip = ({ label, active, onClick, icon: Icon, count }) => (
    <button
        onClick={onClick}
        className={`
            flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-300
            ${active
                ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20 translate-y-[-1px]'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }
        `}
    >
        {Icon && <Icon size={16} className={active ? "text-white" : "text-slate-400"} />}
        {label}
        {count !== undefined && (
            <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-slate-100'}`}>
                {count}
            </span>
        )}
    </button>
);

const Dashboard = ({ initialData, onReset, insights }) => {
    const { logout, user } = useAuth();

    const [searchTerm, setSearchTerm] = useState('');
    const [forecastFactor, setForecastFactor] = useState(0); // 0% to 50%
    const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, RED, YELLOW, GREEN, CRITICAL
    const [selectedItem, setSelectedItem] = useState(null);
    const [showExclusions, setShowExclusions] = useState(false);

    // Bulk Selection
    const [selectedSkus, setSelectedSkus] = useState(new Set());
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [isHubOpen, setIsHubOpen] = useState(false);
    const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);

    // Excluded Items - MUST come before useMorningBriefing
    const { excludedSkus, toggleExclusion, isExcluded, clearExclusions } = useExcludedItems(user);
    const previousSnapshot = aiAnalyst.loadSnapshot();
    const { alerts: briefingAlerts, summary: briefingSummary, hasAlerts } = useMorningBriefing(
        initialData,
        previousSnapshot,
        forecastFactor,
        excludedSkus
    );

    // פתיחה אוטומטית של מרכז המודיעין אם יש התראות או תובנות
    useEffect(() => {
        const hasAny = hasAlerts || (insights && insights.length > 0);
        if (hasAny && previousSnapshot) {
            const lastShown = localStorage.getItem('hub_last_shown');
            const today = new Date().toDateString();
            if (lastShown !== today) {
                setIsHubOpen(true);
                localStorage.setItem('hub_last_shown', today);
            }
        }
    }, [hasAlerts, previousSnapshot, insights]);

    // Sorting
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });



    const [items] = useState(initialData || []);

    const filteredItems = useMemo(() => {
        let res = items;

        // 1. Exclusions
        if (!showExclusions) {
            res = res.filter(item => !isExcluded(item.SKU));
        } else {
            res = res.filter(item => isExcluded(item.SKU));
        }

        // 2. Text Search
        if (searchTerm) {
            res = res.filter(item =>
                item.SKU.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.Description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // 3. Status Filter
        if (statusFilter !== 'ALL') {
            res = res.filter(item => {
                const net = calculateNetStock(item);
                const doi = calculateDOI(net, item.AvgDailySales, forecastFactor);
                const status = getTrafficLight(net, doi);

                if (statusFilter === 'CRITICAL') return net <= 0;
                if (statusFilter === 'LOW') return net > 0 && net < 30; // Strict: 0 < Net < 30
                if (statusFilter === 'RED') return status === 'red';
                if (statusFilter === 'YELLOW') return status === 'yellow';
                if (statusFilter === 'GREEN') return status === 'green';
                if (statusFilter === 'A_ITEMS') return item.Classification === 'A';
                if (statusFilter === 'DEAD') return doi > 180;
                return true;
            });
        }

        if (statusFilter === 'A_ITEMS') {
            // New Logic: Sales > 50 per month (AvgDaily * 30 > 50)
            const highVolumeItems = res.filter(item => (item.AvgDailySales * 30) > 50);

            if (highVolumeItems.length > 0) {
                res = highVolumeItems;
            } else {
                // Fallback: Top 10 by velocity if no items meet the >50 threshold
                res = [...res].sort((a, b) => b.Velocity - a.Velocity).slice(0, 10);
            }
        }

        // 5. Sorting
        if (sortConfig.key) {
            res = [...res].sort((a, b) => {
                let aValue, bValue;

                // Handle calculated fields
                if (sortConfig.key === 'netStock') {
                    aValue = calculateNetStock(a);
                    bValue = calculateNetStock(b);
                } else if (sortConfig.key === 'doi') {
                    aValue = calculateDOI(calculateNetStock(a), a.AvgDailySales, forecastFactor);
                    bValue = calculateDOI(calculateNetStock(b), b.AvgDailySales, forecastFactor);
                } else {
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return res;
    }, [items, searchTerm, statusFilter, excludedSkus, showExclusions, forecastFactor, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Insights
    const stats = useMemo(() => {
        let critical = 0;
        let lowStock = 0;
        let topSellers = 0;
        let slowMovers = 0;

        const activeItems = items.filter(item => !isExcluded(item.SKU));

        // Helper to determine if an item is a top seller for stats counting
        // We use the same >50/month rule here for consistency
        const isTopSeller = (item) => (item.AvgDailySales * 30) > 50;

        // Check if ANY item meets the threshold to decide if we use fallback or not for the count
        const anyHighVolume = activeItems.some(isTopSeller);

        activeItems.forEach(item => {
            const net = calculateNetStock(item);
            const doi = calculateDOI(net, item.AvgDailySales, forecastFactor);

            if (net <= 0) critical++;
            if (net > 0 && net < 30) lowStock++; // Strict Low Stock

            if (anyHighVolume) {
                if (isTopSeller(item)) topSellers++;
            } else {
                // Determine later approx, or just set to 10 if fallback is active effectively?
                // Actually, for the *Count* on the card, if we are in fallback mode, 
                // we are essentially showing the top 10. So the count is 10 (or less if total items < 10).
            }

            if (doi > 180) slowMovers++;
        });

        if (!anyHighVolume) {
            topSellers = Math.min(activeItems.length, 10);
        }

        return { critical, lowStock, topSellers, slowMovers };
    }, [items, forecastFactor, excludedSkus]);

    const handleWhatsAppShare = (item) => {
        const text = `*סטטוס פריט AEG:*
מק"ט: ${item.SKU}
מלאי פיזי: ${item.PhysicalStock}
נמל (הזמנות): ${item.OnOrder}
מכירות: ${formatNumber(item.Velocity)}

*סטטוס*: ${item.PhysicalStock <= 0 ? 'חסר' : 'קיים'}
`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    return (
        <div className="min-h-screen pb-24 bg-slate-50 font-sans">
            {/* --- Sticky Glass Header --- */}
            <header className="sticky top-0 z-40 glass w-full">
                <div className="max-w-7xl mx-auto px-4 py-3">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        {/* Top Row: Logo & Search */}
                        <div className="flex items-center justify-between w-full md:w-auto gap-4">
                            {/* Logo */}
                            <div className="flex items-center gap-3 select-none">
                                <div className="bg-slate-900 text-white w-10 h-10 rounded-xl flex items-center justify-center font-black text-xl shadow-lg shadow-slate-900/20">
                                    A
                                </div>
                                <div>
                                    <h1 className="text-sm font-extrabold text-slate-900 tracking-tight leading-none">AEG STOCK</h1>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Intelligence</p>
                                </div>
                            </div>

                            {/* Mobile Actions */}
                            <div className="flex items-center gap-2 md:hidden">
                                {/* כפתור מרכז המודיעין */}
                                {(hasAlerts || (insights && insights.length > 0)) && (
                                    <button
                                        onClick={() => setIsHubOpen(true)}
                                        className="relative w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center"
                                    >
                                        <Sparkles size={20} />
                                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                            {briefingAlerts.length + (insights?.length || 0)}
                                        </span>
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowExclusions(!showExclusions)}
                                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${showExclusions ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}
                                >
                                    <EyeOff size={20} />
                                </button>
                                <button onClick={logout || onReset} className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center">
                                    <LogOut size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="relative w-full md:w-96 group">
                            <input
                                type="text"
                                placeholder="חיפוש מק״ט או תיאור..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-12 bg-slate-50 border border-slate-200 rounded-2xl pl-12 pr-4 text-right text-slate-900 font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:bg-white transition-all shadow-inner"
                                dir="rtl"
                            />
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors" size={20} />
                        </div>

                        {/* Desktop Actions (כפתורים שהיו רק במובייל) */}
                        <div className="hidden md:flex items-center gap-2">
                            {/* כפתור מרכז המודיעין */}
                            {(hasAlerts || (insights && insights.length > 0)) && (
                                <button
                                    onClick={() => setIsHubOpen(true)}
                                    className="relative h-12 px-4 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-600 flex items-center justify-center gap-2 font-bold transition-colors"
                                >
                                    <Sparkles size={20} />
                                    <span>מרכז המודיעין</span>
                                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                        {briefingAlerts.length + (insights?.length || 0)}
                                    </span>
                                </button>
                            )}
                            <button
                                onClick={() => setIsAnalyticsOpen(true)}
                                className="h-12 px-4 rounded-xl bg-violet-100 hover:bg-violet-200 text-violet-600 flex items-center justify-center gap-2 font-bold transition-colors"
                            >
                                <PieChart size={20} />
                                <span>אנליטיקס</span>
                            </button>
                            <button
                                onClick={() => setShowExclusions(!showExclusions)}
                                className={`h-12 px-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all ${showExclusions ? 'bg-slate-900 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                            >
                                <EyeOff size={20} />
                                <span>{showExclusions ? 'חזרה לרשימה' : 'מוחרגים'}</span>
                                {excludedSkus.length > 0 && !showExclusions && (
                                    <span className="bg-slate-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                        {excludedSkus.length}
                                    </span>
                                )}
                            </button>
                            <button onClick={logout || onReset} className="h-12 px-4 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center gap-2 font-bold transition-colors">
                                <LogOut size={20} />
                                <span>התנתק</span>
                            </button>
                        </div>
                    </div>

                    {/* Horizontal Filter Chips (Scrollable) */}
                    <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-none md:justify-end" dir="rtl">
                        <FilterChip
                            label="הכל"
                            active={statusFilter === 'ALL'}
                            onClick={() => setStatusFilter('ALL')}
                            count={items.length}
                        />
                        <FilterChip
                            label="חוסר קריטי"
                            icon={AlertOctagon}
                            active={statusFilter === 'CRITICAL'}
                            onClick={() => setStatusFilter('CRITICAL')}
                            count={stats.critical}
                        />
                        <FilterChip
                            label="מלאי נמוך"
                            icon={AlertTriangle}
                            active={statusFilter === 'LOW'}
                            onClick={() => setStatusFilter('LOW')}
                            count={stats.lowStock}
                        />
                        <FilterChip
                            label="Top Sellers"
                            icon={TrendingUp}
                            active={statusFilter === 'A_ITEMS'}
                            onClick={() => setStatusFilter('A_ITEMS')}
                        />
                        <FilterChip
                            label="מלאי מת"
                            icon={Clock}
                            active={statusFilter === 'DEAD'}
                            onClick={() => setStatusFilter('DEAD')}
                        />
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">

                {/* --- Bento Grid Stats --- */}
                {/* Only show full stats if no specific status filter is applied, or on desktop always */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 lg:gap-6" dir="rtl">
                    <StatBentoCard
                        title="חוסר קריטי"
                        value={stats.critical}
                        type="critical"
                        icon={AlertOctagon}
                        onClick={() => setStatusFilter('CRITICAL')}
                        isActive={statusFilter === 'CRITICAL'}
                    />
                    <StatBentoCard
                        title="מלאי נמוך"
                        value={stats.lowStock}
                        type="warning"
                        icon={AlertTriangle}
                        onClick={() => setStatusFilter('LOW')}
                        isActive={statusFilter === 'LOW'}
                    />
                    <StatBentoCard
                        title="מוצרים מובילים"
                        value={stats.topSellers}
                        type="success"
                        icon={TrendingUp}
                        onClick={() => setStatusFilter('A_ITEMS')}
                        isActive={statusFilter === 'A_ITEMS'}
                    />
                    <StatBentoCard
                        title="סחורה דלת מכר"
                        value={stats.slowMovers}
                        type="neutral"
                        icon={Clock}
                        onClick={() => setStatusFilter('DEAD')}
                        isActive={statusFilter === 'DEAD'}
                    />
                </div>

                {/* --- Controls: Forecast Slider --- */}
                <div className="card-premium p-6" dir="rtl">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <div className="p-3 bg-slate-100 rounded-xl text-slate-600">
                                <Sliders size={24} />
                            </div>
                            <div>
                                <h3 className="text-dense-title text-base">מקדם ביטחון (Forecast)</h3>
                                <p className="text-dense-subtitle text-xs">החמרת חישוב ימי המלאי (DOI)</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-6 w-full md:w-1/2 bg-slate-50 p-4 rounded-xl border border-slate-100">
                            <span className="text-3xl font-black text-slate-900 tabular-nums w-16 text-center">
                                {Math.round(forecastFactor * 100)}%
                            </span>
                            <div className="relative w-full h-8 flex items-center">
                                <input
                                    type="range"
                                    min="0"
                                    max="0.5"
                                    step="0.05"
                                    value={forecastFactor}
                                    onChange={(e) => setForecastFactor(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-900 hover:accent-slate-700 transition-all"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- List Header --- */}
                <div className="flex items-center justify-between" dir="rtl">
                    <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                        <Package className="text-slate-400" />
                        {showExclusions ? 'פריטים מוחרגים' : 'מלאי בזמן אמת'}
                        <span className="bg-slate-100 text-slate-600 text-sm font-bold px-3 py-1 rounded-full">{filteredItems.length}</span>
                    </h2>

                    {statusFilter !== 'ALL' && (
                        <button
                            onClick={() => setStatusFilter('ALL')}
                            className="bg-slate-900 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg shadow-slate-900/20 active:scale-95 transition-all"
                        >
                            נקה סינון
                        </button>
                    )}
                </div>

                {/* --- Main Content (Table / Cards) --- */}

                {/* Desktop: Table */}
                <div className="hidden lg:block animate-fade-in-up" dir="rtl">
                    <InventoryTable
                        items={filteredItems}
                        forecastFactor={forecastFactor}
                        onRowClick={(item) => setSelectedItem(item)}
                        onToggleExclusion={toggleExclusion}
                        isExcludedMode={showExclusions}
                        selectedItems={selectedSkus}
                        onSelectionChange={setSelectedSkus}
                        sortConfig={sortConfig}
                        requestSort={requestSort}
                    />
                </div>

                {/* Mobile: Cards Stack */}
                <div className="lg:hidden flex flex-col gap-3 pb-20 animate-fade-in-up">
                    {filteredItems.map(item => (
                        <MobileSwipeRow
                            key={item.SKU}
                            onToggleSelect={() => {
                                const newSet = new Set(selectedSkus);
                                if (selectedSkus.has(item.SKU)) newSet.delete(item.SKU);
                                else newSet.add(item.SKU);
                                setSelectedSkus(newSet);
                            }}
                            onExclude={() => toggleExclusion(item.SKU)}
                            isExcluded={isExcluded(item.SKU)}
                            isSelected={selectedSkus.has(item.SKU)}
                        >
                            <InventoryCard
                                item={item}
                                forecastFactor={forecastFactor}
                                onClick={() => setSelectedItem(item)}
                                isSelected={selectedSkus.has(item.SKU)}
                            />
                        </MobileSwipeRow>
                    ))}
                </div>
            </main>

            {/* --- Floating Bottom Bar (Bulk Actions) --- */}
            {selectedSkus.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white pl-2 pr-6 py-2 rounded-full shadow-2xl shadow-slate-900/50 flex items-center gap-4 z-50 animate-fade-in-up max-w-[90vw]">
                    <div className="flex items-center gap-3 pr-2 border-r border-slate-700">
                        <span className="bg-white text-slate-900 font-black text-xs w-6 h-6 rounded-full flex items-center justify-center">
                            {selectedSkus.size}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setIsReportModalOpen(true)}
                            className="p-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white transition-colors"
                        >
                            <MessageCircle size={20} fill="currentColor" />
                        </button>
                        <button
                            onClick={() => {
                                selectedSkus.forEach(sku => toggleExclusion(sku));
                                setSelectedSkus(new Set());
                            }}
                            className="p-3 rounded-full bg-slate-700 hover:bg-slate-600 text-white transition-colors"
                        >
                            <EyeOff size={20} />
                        </button>
                        <button
                            onClick={() => setSelectedSkus(new Set())}
                            className="p-3 rounded-full hover:bg-slate-800 text-slate-400 transition-colors"
                        >
                            <XCircle size={20} />
                        </button>
                    </div>
                </div>
            )}

            {/* --- Footer --- */}
            <footer className="text-center py-8 text-slate-400 text-xs font-semibold tracking-wider uppercase opacity-50">
                AEG Inventory Intelligence v2.0
            </footer>

            {/* Modals */}
            <ItemDetailModal
                item={selectedItem}
                isOpen={!!selectedItem}
                onClose={() => setSelectedItem(null)}
                isExcluded={selectedItem ? isExcluded(selectedItem.SKU) : false}
                onExclude={(sku) => { toggleExclusion(sku); setSelectedItem(null); }}
                isSelected={selectedItem ? selectedSkus.has(selectedItem.SKU) : false}
                onToggleSelect={(sku, isSelected) => {
                    const newSet = new Set(selectedSkus);
                    if (isSelected) newSet.add(sku);
                    else newSet.delete(sku);
                    setSelectedSkus(newSet);
                }}
            />

            <ReportGeneratorModal
                isOpen={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
                selectedItems={items.filter(i => selectedSkus.has(i.SKU))}
                onRemoveItem={(sku) => {
                    const newSet = new Set(selectedSkus);
                    newSet.delete(sku);
                    setSelectedSkus(newSet);
                }}
            />


            {/* מרכז המודיעין - Intelligence Hub */}
            <IntelligenceHub
                isOpen={isHubOpen}
                onClose={() => setIsHubOpen(false)}
                morningAlerts={briefingAlerts}
                morningSummary={briefingSummary}
                insights={insights}
                onGenerateReport={(skus) => {
                    setSelectedSkus(new Set(skus));
                    setIsHubOpen(false);
                    setIsReportModalOpen(true);
                }}
            />

            {/* אנליטיקס - Treemap */}
            <AnalyticsModal
                isOpen={isAnalyticsOpen}
                onClose={() => setIsAnalyticsOpen(false)}
                items={filteredItems}
                forecastFactor={forecastFactor}
                onItemClick={(item) => setSelectedItem(item)}
            />
        </div>
    );
};

export default Dashboard;
